import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { Box, IconButton, Popover, Table, TableBody, TableRow, TableCell, Typography, colors } from '@mui/material';
import { FC, MouseEvent, memo, useCallback, useState, useMemo } from 'react';
import { TRUCK_UNSET_LABEL, unsetColorFor } from 'src/constants/assignmentLabels';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';

type Props = {
  filteredNotAllocReasons: PlanningsNotAllocReason[];
  notAllocDeliveries: PlanningsDeliveryEntity[];
  notAllocTrucks: PlanningsTruckEntity[];
  notAllocDrivers: PlanningsDriverEntity[];
  /**
   * 'badge' で「未割当理由 (N件)」のテキストバッジ + 車両/理由の 2 列にする。
   * 既定の 'icon' はインフォアイコン + 車両/ドライバー/理由の 3 列。
   *
   * この (V3Plans 版) の消費者は 3 箇所 (LaneCard.tsx / ConvenienceDispatchUnassignedList.tsx
   * が variant="badge"、DisplayPropertiesSelectableUnallocatedOrder.presenter.tsx が既定の
   * 'icon' を使用)。同名の V2Plans 版 (src/pages/V2Plans/presenters/NotAllocReasonPresenter.tsx)
   * は別ファイルで、V2Plans / V2OrdersPresenter / V2ShipmentOrderLegsPresenter が使う。
   */
  variant?: 'icon' | 'badge';
};

export const NotAllocReasonPresenter: FC<Props> = memo(({
  filteredNotAllocReasons,
  notAllocDeliveries,
  notAllocTrucks,
  notAllocDrivers,
  variant = 'icon',
}) => {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement>(null);

  const handlePopoverOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setPopoverAnchorEl(event.currentTarget);
  }, []);

  const handlePopoverClose = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setPopoverAnchorEl(null);
  }, []);

  const handlePopoverClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const notAllocReasons = useMemo(() => {
    const processed: { truckId: number, driverId: number, reason: string }[] = [];
    return filteredNotAllocReasons.filter((reason) => {
      const delivery = notAllocDeliveries.find((it) => it.id === reason.delivery_id);
      const truck = delivery ? notAllocTrucks.find((it) => it.id === delivery.truckId) : null;
      const driver = delivery ? notAllocDrivers.find((it) => it.id === delivery.driverId) : null;

      if (processed.findIndex((it) => it.truckId === truck?.id && it.driverId === driver?.id && it.reason === reason.message) >= 0) {
        return false;
      }
      processed.push({ truckId: truck?.id, driverId: driver?.id, reason: reason.message });
      return true;
    });
  }, [filteredNotAllocReasons, notAllocDeliveries, notAllocDrivers, notAllocTrucks]);

  /**
   * 2 列版のエントリ。車両名へ射影してから車両+理由で重複排除する。
   *
   * 既存の 3 列版は truckId + driverId + reason で排除しているが、2 列版は
   * ドライバーを隠すため、同じ車両・同じ理由でドライバーだけ違う 2 件が
   * 見た目まったく同じ行として 2 行並び、件数バッジも実際の行数とずれる。
   *
   * 車両名は必ず非空にする。マスタ未取得や車番未設定で空文字になると
   * バッジの車両欄が空欄になり壊れて見えるため。非空フォールバックにすると
   * 解決できない車両は「車両未設定 + 同じ理由」に揃って 1 行にまとまるが、
   * 見た目が同じ行を 1 行にするのは 2 列版の意図どおり。
   */
  const badgeEntries = useMemo(() => {
    const seen = new Set<string>();
    const entries: { vehicle: string; reason: string }[] = [];
    filteredNotAllocReasons.forEach((reason) => {
      const delivery = notAllocDeliveries.find((it) => it.id === reason.delivery_id);
      const truck = delivery ? notAllocTrucks.find((it) => it.id === delivery.truckId) : null;
      const vehicle = truck?.temporary
        ? TRUCK_UNSET_LABEL
        : truck?.licensePlateValue || TRUCK_UNSET_LABEL;
      // 重複排除キーは「実際に見える文字列」で作る。truckId で作ると、別 id が
      // 同じ表示名に解決されたときに同一の行が 2 行並ぶ。
      const key = `${vehicle}::${reason.message}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push({ vehicle, reason: reason.message });
    });
    return entries;
  }, [filteredNotAllocReasons, notAllocDeliveries, notAllocTrucks]);

  const isBadge = variant === 'badge';
  // 理由が無いバッジは出さない(アイコン版は呼び出し側が hasReasons で制御しており挙動を変えない)。
  if (isBadge && badgeEntries.length === 0) return null;

  return (
    <>
      {isBadge ? (
        <Box
          component="button"
          type="button"
          // カーソルを当てただけで読めるようにする（クリックだと 1 件ずつ開いて閉じることになる）。
          // クリックでも開くのは、キーボードや触ってから読む操作を塞がないため
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          onClick={handlePopoverOpen}
          aria-label="未割当理由"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: '2px',
            border: 'none',
            borderRadius: '3px',
            bgcolor: 'hsl(var(--status-warning-accent) / 0.14)',
            color: 'hsl(var(--status-warning-foreground))',
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          <WarningAmberRounded sx={{ fontSize: 12 }} />
          {`未割当理由 (${badgeEntries.length}件)`}
        </Box>
      ) : (
        <IconButton onClick={handlePopoverOpen} aria-label="未割当理由">
          <InfoOutlinedIcon sx={{ color: colors.blue[900] }} />
        </IconButton>
      )}
      <Popover
        id={popoverAnchorEl ? 'not-alloc-reason-popover' : undefined}
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        onClick={handlePopoverClick}
        // 右未割当リストは画面の右端にあるので、右へ開くと panel が画面外に寄って
        // 端で折り返す。左へ開いてリストの上に載せる
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        // ホバーで出すパネルはカーソルを奪わない。奪うと下のカードを触れなくなる
        sx={isBadge ? { pointerEvents: 'none' } : undefined}
        slotProps={isBadge ? { paper: { sx: { bgcolor: 'transparent', boxShadow: 3 } } } : undefined}
        disableRestoreFocus={isBadge}
      >
        {isBadge ? (
          // 地図の上に重なるので濃色にする。薄い背景だと下の地図と混ざって読めない（サンプル準拠）
          <Box
            data-testid="not-alloc-reason-panel"
            sx={{
              p: 1.5,
              maxWidth: 360,
              fontSize: 11,
              bgcolor: 'hsl(222 47% 20%)',
              color: '#fff',
              borderRadius: 1,
            }}
          >
            <Box sx={{ mb: 1, fontSize: 12, fontWeight: 700 }}>自動配車で割当できませんでした</Box>
            {badgeEntries.map((entry) => (
              <Box key={`${entry.vehicle}::${entry.reason}`} sx={{ display: 'flex', gap: 1.5, mt: 0.75 }}>
                {/* 車両名は理由より弱く見せる。行頭が揃うよう幅を固定する */}
                <Box sx={{ flexShrink: 0, minWidth: 72, color: 'rgba(255,255,255,0.6)' }}>{entry.vehicle}</Box>
                <Box sx={{ flex: 1 }}>{entry.reason}</Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Table>
            <TableBody>
              {notAllocReasons.map((reason, index) => {
                const delivery = notAllocDeliveries.find((it) => it.id === reason.delivery_id);
                const truck = delivery ? notAllocTrucks.find((it) => it.id === delivery.truckId) : null;
                const driver = delivery ? notAllocDrivers.find((it) => it.id === delivery.driverId) : null;

                return (
                  <TableRow key={[delivery?.id, truck?.id, driver?.id, index].join('-')}>
                    <TableCell sx={{ p: 1 }}><Typography noWrap sx={{ color: unsetColorFor(truck?.temporary ? TRUCK_UNSET_LABEL : truck?.licensePlateValue) }}>{truck?.temporary ? TRUCK_UNSET_LABEL : truck?.licensePlateValue}</Typography></TableCell>
                    <TableCell sx={{ p: 1 }}><Typography noWrap sx={{ color: unsetColorFor(driver?.temporary ? 'ドライバー未設定' : driver?.name) }}>{driver?.temporary ? 'ドライバー未設定' : driver?.name}</Typography></TableCell>
                    <TableCell sx={{ p: 1 }}><Typography noWrap>{reason?.message}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Popover>
    </>
  );
});
